/**
 * useEgoConfigForm Hook
 *
 * Manages form state for the eGO device configuration form.
 * Handles site ID input, bay fetching via fetchSiteConfig, bay/distanceUnit
 * dropdowns, validation, and save via upsertEgoDeviceConfig.
 *
 * Requirements: 6.1–6.6, 7.1–7.6
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchSiteConfig, SiteNotFoundError } from '../../site/api/siteApiClient';
import { upsertEgoDeviceConfig } from '../api/egoApiClient';
import { useAuth } from '../../auth/useAuth';
import { getAppConfig } from '../../config/appConfig';
import type { DistanceUnit, EgoConfigFormState, UpsertEgoConfigRequest } from '../models/egoModels';

export interface UseEgoConfigFormOptions {
  readonly deviceId: string;
  readonly initialSiteId?: string;
  readonly initialBayId?: string;
  readonly initialDistanceUnit?: DistanceUnit;
  readonly onSaveSuccess?: () => void;
}

export interface UseEgoConfigFormResult {
  readonly formState: EgoConfigFormState;
  readonly setSiteId: (value: string) => void;
  readonly setBayId: (value: string) => void;
  readonly setDistanceUnit: (value: DistanceUnit) => void;
  readonly availableBays: readonly string[];
  readonly isBaysLoading: boolean;
  readonly baysError: string | null;
  readonly handleSave: () => Promise<void>;
  readonly isSaving: boolean;
  readonly saveError: string | null;
  readonly saveSuccess: boolean;
  readonly validationErrors: Record<string, string>;
}

export function useEgoConfigForm(options: UseEgoConfigFormOptions): UseEgoConfigFormResult {
  const { deviceId, initialSiteId = '', initialBayId = '', initialDistanceUnit = 'Meters', onSaveSuccess } = options;
  const { getAccessToken } = useAuth();

  // Use a ref for getAccessToken to avoid re-triggering the bay fetch effect
  // when the auth context provides a new function reference
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  const [formState, setFormState] = useState<EgoConfigFormState>({
    siteId: initialSiteId,
    bayId: initialBayId,
    distanceUnit: initialDistanceUnit,
  });

  const [availableBays, setAvailableBays] = useState<readonly string[]>([]);
  const [isBaysLoading, setIsBaysLoading] = useState(false);
  const [baysError, setBaysError] = useState<string | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Track whether this is the initial mount to skip fetching bays for the initial value
  const isInitialMount = useRef(true);

  const setSiteId = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, siteId: value, bayId: '' }));
    setAvailableBays([]);
    setBaysError(null);
    setSaveSuccess(false);
  }, []);

  const setBayId = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, bayId: value }));
    setSaveSuccess(false);
  }, []);

  const setDistanceUnit = useCallback((value: DistanceUnit) => {
    setFormState((prev) => ({ ...prev, distanceUnit: value }));
    setSaveSuccess(false);
  }, []);

  // Fetch bays when siteId changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!formState.siteId.trim()) {
        return;
      }
    }

    const trimmedSiteId = formState.siteId.trim();
    if (!trimmedSiteId) {
      setAvailableBays([]);
      setBaysError(null);
      return;
    }

    let cancelled = false;

    async function loadBays() {
      setIsBaysLoading(true);
      setBaysError(null);
      setAvailableBays([]);

      try {
        const appConfig = getAppConfig();
        const siteConfig = await fetchSiteConfig(trimmedSiteId, {
          apiHost: appConfig.apiHost,
          getAccessToken: getAccessTokenRef.current,
        });
        if (!cancelled) {
          const fixedBayIds = siteConfig.bays.fixedBays.map((b) => b.id);
          if (fixedBayIds.length === 0) {
            setBaysError('This site has no fixed bays available');
          } else {
            // Ensure the initial bay ID is included in the list even if the
            // site config doesn't contain it (device already has it configured)
            if (initialBayId && !fixedBayIds.includes(initialBayId)) {
              setAvailableBays([initialBayId, ...fixedBayIds]);
            } else {
              setAvailableBays(fixedBayIds);
            }
          }
          setIsBaysLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          if (err instanceof SiteNotFoundError) {
            setBaysError('Site not found. Check the site ID.');
          } else {
            setBaysError('Failed to load site configuration');
          }
          setIsBaysLoading(false);
        }
      }
    }

    loadBays();

    return () => {
      cancelled = true;
    };
  }, [formState.siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = useCallback(async () => {
    // Validate
    const errors: Record<string, string> = {};
    if (!formState.siteId.trim()) {
      errors.siteId = 'Site ID is required';
    }
    if (!formState.bayId) {
      errors.bayId = 'Bay ID is required';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const appConfig = getAppConfig();
      const body: UpsertEgoConfigRequest = {
        deviceId,
        config: {
          siteId: formState.siteId.trim(),
          bayId: formState.bayId,
          distanceUnit: formState.distanceUnit,
        },
      };
      await upsertEgoDeviceConfig(deviceId, body, {
        apiHost: appConfig.apiHost,
        getAccessToken: getAccessTokenRef.current,
      });
      setIsSaving(false);
      setSaveSuccess(true);
      onSaveSuccess?.();
    } catch {
      setIsSaving(false);
      setSaveError('Failed to save configuration');
    }
  }, [deviceId, formState]);

  return {
    formState,
    setSiteId,
    setBayId,
    setDistanceUnit,
    availableBays,
    isBaysLoading,
    baysError,
    handleSave,
    isSaving,
    saveError,
    saveSuccess,
    validationErrors,
  };
}
